import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Form, Grid, Icon, Input, Modal, TextArea } from "semantic-ui-react";

import { Dropdown } from "components";
import { useShowPriceInVat } from "hooks";
import { COMMUNICATION_CHANNELS, PIN_TYPES, SMS_GATEWAYS } from "models";
import {
  CustomerCommunicationInterventions,
  CustomerCommunicationPrice,
  CustomerCommunicationReplacements,
  RepairOverviewInterventions
} from "modules/AppointmentDetails/components/CustomerCommunicationModal/components";
import "modules/AppointmentDetails/components/CustomerCommunicationModal/CustomerCommunicationModal.scss";
import { useCustomerCommunication } from "modules/AppointmentDetails/components/CustomerCommunicationModal/hooks";
import { useAppointmentDealer, useAppointmentLocation, useRealTimeOrderedChecks } from "modules/AppointmentDetails/hooks";
import { isCarReadyOrQualityCheck } from "utils/appointmentUtils";
import { getInterventionPriceWithVAT, getTotals, validateEmail, validatePhoneNr } from "utils/common";
import { formatPrice } from "utils/formatPrice";

import type { DropdownItemProps } from "components";
import type { TFunction } from "i18next";
import type { Appointment, CommunicationEvent, CommunicationReceiver, Customer, DealerLocation, Intervention } from "models";

export type Receivers = {
  destination: string;
  channel: COMMUNICATION_CHANNELS;
};

type CustomerCommunicationModalProps = {
  appointment: Appointment;
  onClose?: () => void;
  send: CUSTOMER_COMMUNICATIONS;
};

export enum CUSTOMER_COMMUNICATIONS {
  DiagnoseOverview = 1,
  RepairOverview,
  OnlineCheckin
}

type FormState = {
  selectedCustomerID: number;
  parsedCustomerEmail: string;
  selectedEmail: string;
  manualEmailSelected: boolean;
  manuallyEnteredEmail: string;
  invalidEmail: boolean;
  invalidManualEmail: boolean;
  parsedCustomerPhone: string;
  selectedPhone: string;
  manualPhoneNrSelected: boolean;
  manuallyEnteredPhoneNr: string;
  invalidPhone: boolean;
  parsedWhatsApp: string;
  selectedWhatsApp: string;
  manualWhatsAppSelected: boolean;
  manuallyEnteredWhatsApp: string;
  invalidWhatsApp: boolean;
  invalidCustomer: boolean;
  invalidContent: boolean;
  note: string;
};

type PreferredChannelIconProps = { isPreferred: boolean };

const MANUAL_ENTRY_VALUE = ";-1";
const DO_NOT_SEND_BY_VALUE = ";-2";

export const isHiddenPriceIntervention = (intervention: Intervention, location?: DealerLocation) => {
  const pinTypeId = intervention.pin_history?.[0]?.pin_type_id;
  if (pinTypeId === PIN_TYPES.Warranty && location?.hide_warranty_intervention_price) return true;
  if (pinTypeId === PIN_TYPES.Recall && location?.hide_recall_intervention_price) return true;
  return false;
};

export const getRenderedInterventionPrice = (intervention: Intervention, t: TFunction, showVat: boolean, location?: DealerLocation) => {
  if (isHiddenPriceIntervention(intervention, location)) {
    const pinTypeId = intervention.pin_history?.[0]?.pin_type_id;
    return pinTypeId === PIN_TYPES.Warranty ? t("v8_warranty", "Warranty") : t("v8_recall", "Recall");
  }

  return `€ ${showVat ? formatPrice(getInterventionPriceWithVAT(intervention, location)) : formatPrice(intervention.price)}`;
};

const PreferredChannelIcon = ({ isPreferred }: PreferredChannelIconProps) => {
  if (!isPreferred) return;
  return (
    <span className="CustomerCommunicationModal-preferred-channel">
      <Icon name="star" />
    </span>
  );
};

// TODO: with 1000+ lines already, this is an obvious candidate for refactor; very messy having all this logic inside one component
// should be split up into multiple hooks and components and revisit logic how to deduplicate and simplify all this mess
export const CustomerCommunicationModal = ({ appointment, send, onClose }: CustomerCommunicationModalProps) => {
  const { t } = useTranslation();

  const selectedLocation = useAppointmentLocation(appointment);
  const appointmentDealer = useAppointmentDealer(appointment);
  const saveCustomerCommunicationMutation = useCustomerCommunication();
  const { checks } = useRealTimeOrderedChecks(appointment.id);
  const showPriceInVat = useShowPriceInVat();

  const [showInterventionsVat, setShowInterventionsVat] = useState(showPriceInVat);
  const [showReplacementsVat, setShowReplacementsVat] = useState(showPriceInVat);
  const [isLoading, setIsLoading] = useState(false);
  const [isInvalidEmail, setIsInvalidEmail] = useState(false);
  const [emailOptions, setEmailOptions] = useState<DropdownItemProps<string>[]>([]);
  const [phoneOptions, setPhoneOptions] = useState<DropdownItemProps<string>[]>([]);
  const [whatsAppOptions, setWhatsAppOptions] = useState<DropdownItemProps<string>[]>([]);

  const customerCommunicationLastEvent = appointment.customer_communication?.events
    ?.filter((c: CommunicationEvent) => Array.isArray(c.receivers) && c.receivers.length > 0)
    .sort((ev1: CommunicationEvent, ev2: CommunicationEvent) => {
      if (ev1.created_on > ev2.created_on) return -1;
      if (ev1.created_on < ev2.created_on) return 1;
      return 0;
    })
    .find((item: CommunicationEvent) => item.receivers?.some((receiver: CommunicationReceiver) => receiver.destination));

  const lastEmailReceiver = customerCommunicationLastEvent?.receivers?.find((receiver: CommunicationReceiver) => receiver.channel === COMMUNICATION_CHANNELS.Email);
  const lastPhoneReceiver = customerCommunicationLastEvent?.receivers?.find((receiver: CommunicationReceiver) => receiver.channel === COMMUNICATION_CHANNELS.SMS);
  const lastWhatsAppReceiver = customerCommunicationLastEvent?.receivers?.find((receiver: CommunicationReceiver) => receiver.channel === COMMUNICATION_CHANNELS.WhatsApp);

  const [formState, setFormState] = useState<FormState>({
    selectedCustomerID: appointment.customer_communication?.customer_id ?? 0,
    parsedCustomerEmail: lastEmailReceiver?.destination ?? "",
    selectedEmail: DO_NOT_SEND_BY_VALUE,
    manualEmailSelected: false,
    manuallyEnteredEmail: "",
    invalidEmail: false,
    invalidManualEmail: false,
    parsedCustomerPhone: lastPhoneReceiver?.destination ?? "",
    selectedPhone: DO_NOT_SEND_BY_VALUE,
    manualPhoneNrSelected: false,
    manuallyEnteredPhoneNr: "",
    invalidPhone: false,
    parsedWhatsApp: lastWhatsAppReceiver?.destination ?? "",
    selectedWhatsApp: DO_NOT_SEND_BY_VALUE,
    manualWhatsAppSelected: false,
    manuallyEnteredWhatsApp: "",
    invalidWhatsApp: false,
    invalidCustomer: false,
    invalidContent: false,
    note: appointment.customer_communication?.note ?? ""
  });

  const shouldRenderPhoneNumberField = !appointment.internal && appointmentDealer?.sms_gateway_id && appointmentDealer.sms_gateway_id > SMS_GATEWAYS.Disabled;
  const shouldRenderWhatsAppField = Boolean(selectedLocation?.is_bird_whats_app_webhook_set);

  const preferredChannel =
    appointment.customer_driver?.preferred_communication_channel ??
    appointment.customer_owner?.preferred_communication_channel ??
    appointment.customer_contract?.preferred_communication_channel;

  const getBlockCommunicationSuffix = (customer: Customer) => (customer.block_communication ? ` - ${t("v8_communication_blocked", "Communication blocked")}` : "");

  const getIsInternalCommunicationSuffix = (customer: Customer) => (customer.is_internal ? ` - ${t("v8_internal", "Internal")}` : "");

  const getCustomerOptions = (appointment: Appointment) => {
    const customerOptions: DropdownItemProps[] = [];
    if (appointment.customer_driver?.dms_nr) {
      customerOptions.push({
        key: `${appointment.customer_driver.id}`,
        text: `${appointment.customer_driver.firstname} ${appointment.customer_driver.surname}`,
        value: appointment.customer_driver.id,
        disabled: appointment.customer_driver.block_communication || appointment.customer_driver.is_internal,
        description: t("v8_driver", "Driver") + getBlockCommunicationSuffix(appointment.customer_driver) + getIsInternalCommunicationSuffix(appointment.customer_driver)
      });
    }
    if (appointment.customer_owner?.dms_nr) {
      customerOptions.push({
        key: `${appointment.customer_owner.id}`,
        text: `${appointment.customer_owner.firstname} ${appointment.customer_owner.surname}`,
        value: appointment.customer_owner.id,
        disabled: appointment.customer_owner.block_communication || appointment.customer_owner.is_internal,
        description: t("v8_owner", "Owner") + getBlockCommunicationSuffix(appointment.customer_owner) + getIsInternalCommunicationSuffix(appointment.customer_owner)
      });
    }

    if (appointment.customer_contract?.dms_nr) {
      customerOptions.push({
        key: `${appointment.customer_contract.id}`,
        text: `${appointment.customer_contract.firstname} ${appointment.customer_contract.surname}`,
        value: appointment.customer_contract.id,
        disabled: appointment.customer_contract.block_communication || appointment.customer_contract.is_internal,
        description:
          t("v8_contractor", "Contractor") + getBlockCommunicationSuffix(appointment.customer_contract) + getIsInternalCommunicationSuffix(appointment.customer_contract)
      });
    }

    return customerOptions;
  };

  const getDefaultValueForEmailAndPhone = (customerId: number, customerEmailOrPhone: string, defaultValue: string, isEmail: boolean) => {
    const getLastReceiver = () => {
      if (isEmail) return lastEmailReceiver;
      else if (lastPhoneReceiver) return lastPhoneReceiver;
      return lastWhatsAppReceiver;
    };

    const lastReceiver = getLastReceiver();
    if (formState.selectedCustomerID === customerId && lastReceiver?.destination === customerEmailOrPhone) return `${customerEmailOrPhone};${customerId}`;
    return defaultValue;
  };

  const setEmailOptionsAndValue = (appointment: Appointment) => {
    const emailOptions: DropdownItemProps<string>[] = [];
    let emailValue = "";
    let manualEmailSelected = false;
    let manuallyEnteredEmail = "";

    emailOptions.push({
      key: DO_NOT_SEND_BY_VALUE,
      text: t("v8_do_not_send_by_email", "Do not send by email"),
      value: DO_NOT_SEND_BY_VALUE
    });

    if (!lastEmailReceiver?.destination) emailValue = DO_NOT_SEND_BY_VALUE;

    if (appointment.customer_driver?.email_business) {
      emailOptions.push({
        key: `${appointment.customer_driver.email_business};${appointment.customer_driver.id}`,
        text: appointment.customer_driver.email_business,
        value: `${appointment.customer_driver.email_business};${appointment.customer_driver.id}`,
        disabled: appointment.customer_driver.block_communication || appointment.customer_driver.is_internal,
        description:
          t("v8_driver_business", "Driver business") +
          getBlockCommunicationSuffix(appointment.customer_driver) +
          getIsInternalCommunicationSuffix(appointment.customer_driver)
      });
      emailValue = getDefaultValueForEmailAndPhone(appointment.customer_driver.id, appointment.customer_driver.email_business, emailValue, true);
    }

    if (appointment.customer_driver?.email_private) {
      emailOptions.push({
        key: `${appointment.customer_driver.email_private};${appointment.customer_driver.id}`,
        text: appointment.customer_driver.email_private,
        value: `${appointment.customer_driver.email_private};${appointment.customer_driver.id}`,
        disabled: appointment.customer_driver.block_communication || appointment.customer_driver.is_internal,
        description:
          t("v8_driver_private", "Driver private") +
          getBlockCommunicationSuffix(appointment.customer_driver) +
          getIsInternalCommunicationSuffix(appointment.customer_driver)
      });
      emailValue = getDefaultValueForEmailAndPhone(appointment.customer_driver.id, appointment.customer_driver.email_private, emailValue, true);
    }

    if (appointment.customer_contract?.email_business) {
      emailOptions.push({
        key: `${appointment.customer_contract.email_business};${appointment.customer_contract.id}`,
        text: appointment.customer_contract.email_business,
        value: `${appointment.customer_contract.email_business};${appointment.customer_contract.id}`,
        disabled: appointment.customer_contract.block_communication || appointment.customer_contract.is_internal,
        description:
          t("v8_contract_business", "Contract business") +
          getBlockCommunicationSuffix(appointment.customer_contract) +
          getIsInternalCommunicationSuffix(appointment.customer_contract)
      });
      emailValue = getDefaultValueForEmailAndPhone(appointment.customer_contract.id, appointment.customer_contract.email_business, emailValue, true);
    }

    if (appointment.customer_contract?.email_private) {
      emailOptions.push({
        key: `${appointment.customer_contract.email_private};${appointment.customer_contract.id}`,
        text: appointment.customer_contract.email_private,
        value: `${appointment.customer_contract.email_private};${appointment.customer_contract.id}`,
        disabled: appointment.customer_contract.block_communication || appointment.customer_contract.is_internal,
        description:
          t("v8_contract_private", "Contract private") +
          getBlockCommunicationSuffix(appointment.customer_contract) +
          getIsInternalCommunicationSuffix(appointment.customer_contract)
      });
      emailValue = getDefaultValueForEmailAndPhone(appointment.customer_contract.id, appointment.customer_contract.email_private, emailValue, true);
    }

    if (appointment.customer_owner?.email_business) {
      emailOptions.push({
        key: `${appointment.customer_owner.email_business};${appointment.customer_owner.id}`,
        text: appointment.customer_owner.email_business,
        value: `${appointment.customer_owner.email_business};${appointment.customer_owner.id}`,
        disabled: appointment.customer_owner.block_communication || appointment.customer_owner.is_internal,
        description:
          t("v8_owner_business", "Owner business") +
          getBlockCommunicationSuffix(appointment.customer_owner) +
          getIsInternalCommunicationSuffix(appointment.customer_owner)
      });
      emailValue = getDefaultValueForEmailAndPhone(appointment.customer_owner.id, appointment.customer_owner.email_business, emailValue, true);
    }

    if (appointment.customer_owner?.email_private) {
      emailOptions.push({
        key: `${appointment.customer_owner.email_private};${appointment.customer_owner.id}`,
        text: appointment.customer_owner.email_private,
        value: `${appointment.customer_owner.email_private};${appointment.customer_owner.id}`,
        disabled: appointment.customer_owner.block_communication || appointment.customer_owner.is_internal,
        description:
          t("v8_owner_private", "Owner private") + getBlockCommunicationSuffix(appointment.customer_owner) + getIsInternalCommunicationSuffix(appointment.customer_owner)
      });
      emailValue = getDefaultValueForEmailAndPhone(appointment.customer_owner.id, appointment.customer_owner.email_private, emailValue, true);
    }

    emailOptions.push({
      key: MANUAL_ENTRY_VALUE,
      text: t("v8_manually_enter_email", "Manually enter email"),
      value: MANUAL_ENTRY_VALUE
    });

    if (!emailValue && lastEmailReceiver?.destination) {
      emailValue = MANUAL_ENTRY_VALUE;
      manualEmailSelected = true;
      manuallyEnteredEmail = lastEmailReceiver.destination || "";
    }

    setEmailOptions(emailOptions);
    setFormState(pre => ({ ...pre, selectedEmail: emailValue, manualEmailSelected, manuallyEnteredEmail }));
  };

  const setPhoneOptionsAndValue = (appointment: Appointment) => {
    const phoneOptions: DropdownItemProps<string>[] = [];
    let phoneValue = "";
    let manualPhoneNrSelected = false;
    let manuallyEnteredPhoneNr = "";

    phoneOptions.push({
      key: DO_NOT_SEND_BY_VALUE,
      text: t("v8_do_not_send_by_SMS", "Do not send by SMS"),
      value: DO_NOT_SEND_BY_VALUE
    });

    phoneOptions.push({
      key: MANUAL_ENTRY_VALUE,
      text: t("v8_manually_enter_phone", "Manually enter phone number"),
      value: MANUAL_ENTRY_VALUE
    });

    if (appointment.customer_driver?.id) {
      if (appointment.customer_driver.tel_mobile_business) {
        phoneOptions.push({
          key: `${appointment.customer_driver.tel_mobile_business};${appointment.customer_driver.id}`,
          text: appointment.customer_driver.tel_mobile_business,
          value: `${appointment.customer_driver.tel_mobile_business};${appointment.customer_driver.id}`,
          disabled: appointment.customer_driver.block_communication || appointment.customer_driver.is_internal,
          description:
            t("v8_driver_business", "Driver business") +
            getBlockCommunicationSuffix(appointment.customer_driver) +
            getIsInternalCommunicationSuffix(appointment.customer_driver)
        });
        phoneValue = getDefaultValueForEmailAndPhone(appointment.customer_driver.id, appointment.customer_driver.tel_mobile_business, phoneValue, false);
      }

      if (appointment.customer_driver.tel_mobile_private) {
        phoneOptions.push({
          key: `${appointment.customer_driver.tel_mobile_private};${appointment.customer_driver.id}`,
          text: appointment.customer_driver.tel_mobile_private,
          value: `${appointment.customer_driver.tel_mobile_private};${appointment.customer_driver.id}`,
          disabled: appointment.customer_driver.block_communication || appointment.customer_driver.is_internal,
          description:
            t("v8_driver_private", "Driver private") +
            getBlockCommunicationSuffix(appointment.customer_driver) +
            getIsInternalCommunicationSuffix(appointment.customer_driver)
        });
        phoneValue = getDefaultValueForEmailAndPhone(appointment.customer_driver.id, appointment.customer_driver.tel_mobile_private, phoneValue, false);
      }
    }

    if (appointment.customer_contract?.id) {
      if (appointment.customer_contract.tel_mobile_business) {
        phoneOptions.push({
          key: `${appointment.customer_contract.tel_mobile_business};${appointment.customer_contract.id}`,
          text: appointment.customer_contract.tel_mobile_business,
          value: `${appointment.customer_contract.tel_mobile_business};${appointment.customer_contract.id}`,
          disabled: appointment.customer_contract.block_communication || appointment.customer_contract.is_internal,
          description:
            t("v8_contract_business", "Contract business") +
            getBlockCommunicationSuffix(appointment.customer_contract) +
            getIsInternalCommunicationSuffix(appointment.customer_contract)
        });
        phoneValue = getDefaultValueForEmailAndPhone(appointment.customer_contract.id, appointment.customer_contract.tel_mobile_business, phoneValue, false);
      }

      if (appointment.customer_contract.tel_mobile_private) {
        phoneOptions.push({
          key: `${appointment.customer_contract.tel_mobile_private};${appointment.customer_contract.id}`,
          text: appointment.customer_contract.tel_mobile_private,
          value: `${appointment.customer_contract.tel_mobile_private};${appointment.customer_contract.id}`,
          disabled: appointment.customer_contract.block_communication || appointment.customer_contract.is_internal,
          description:
            t("v8_contract_private", "Contract private") +
            getBlockCommunicationSuffix(appointment.customer_contract) +
            getIsInternalCommunicationSuffix(appointment.customer_contract)
        });
        phoneValue = getDefaultValueForEmailAndPhone(appointment.customer_contract.id, appointment.customer_contract.tel_mobile_private, phoneValue, false);
      }
    }

    if (appointment.customer_owner?.id) {
      if (appointment.customer_owner.tel_mobile_business) {
        phoneOptions.push({
          key: `${appointment.customer_owner.tel_mobile_business};${appointment.customer_owner.id}`,
          text: appointment.customer_owner.tel_mobile_business,
          value: `${appointment.customer_owner.tel_mobile_business};${appointment.customer_owner.id}`,
          disabled: appointment.customer_owner.block_communication || appointment.customer_owner.is_internal,
          description:
            t("v8_owner_business", "Owner business") +
            getBlockCommunicationSuffix(appointment.customer_owner) +
            getIsInternalCommunicationSuffix(appointment.customer_owner)
        });
        phoneValue = getDefaultValueForEmailAndPhone(appointment.customer_owner.id, appointment.customer_owner.tel_mobile_business, phoneValue, false);
      }

      if (appointment.customer_owner.tel_mobile_private) {
        phoneOptions.push({
          key: `${appointment.customer_owner.tel_mobile_private};${appointment.customer_owner.id}`,
          text: appointment.customer_owner.tel_mobile_private,
          value: `${appointment.customer_owner.tel_mobile_private};${appointment.customer_owner.id}`,
          disabled: appointment.customer_owner.block_communication || appointment.customer_owner.is_internal,
          description:
            t("v8_owner_private", "Owner private") +
            getBlockCommunicationSuffix(appointment.customer_owner) +
            getIsInternalCommunicationSuffix(appointment.customer_owner)
        });
        phoneValue = getDefaultValueForEmailAndPhone(appointment.customer_owner.id, appointment.customer_owner.tel_mobile_private, phoneValue, false);
      }
    }

    if (!phoneValue && !lastPhoneReceiver?.destination) phoneValue = DO_NOT_SEND_BY_VALUE;
    else if (lastPhoneReceiver?.destination && !phoneValue) {
      phoneValue = MANUAL_ENTRY_VALUE;
      manualPhoneNrSelected = true;
      manuallyEnteredPhoneNr = lastPhoneReceiver.destination || "";
    }

    setPhoneOptions(phoneOptions);
    setFormState(pre => ({ ...pre, selectedPhone: phoneValue, manualPhoneNrSelected, manuallyEnteredPhoneNr }));
  };

  const setWhatsAppOptionsAndValue = (appointment: Appointment) => {
    const options: DropdownItemProps<string>[] = [];
    let whatsAppValue = "";
    let manualWhatsAppSelected = false;
    let manuallyEnteredWhatsApp = "";

    options.push({
      key: DO_NOT_SEND_BY_VALUE,
      text: t("v8_do_not_send_by_whatsapp", "Do not send by WhatsApp"),
      value: DO_NOT_SEND_BY_VALUE
    });

    options.push({
      key: MANUAL_ENTRY_VALUE,
      text: t("v8_manually_enter_whatsapp_phone", "Manually enter WhatsApp phone number"),
      value: MANUAL_ENTRY_VALUE
    });

    if (appointment.customer_driver?.id) {
      if (appointment.customer_driver.tel_mobile_business) {
        options.push({
          key: `${appointment.customer_driver.tel_mobile_business};${appointment.customer_driver.id}`,
          text: appointment.customer_driver.tel_mobile_business,
          value: `${appointment.customer_driver.tel_mobile_business};${appointment.customer_driver.id}`,
          disabled: appointment.customer_driver.block_communication || appointment.customer_driver.is_internal,
          description:
            t("v8_driver_business", "Driver business") +
            getBlockCommunicationSuffix(appointment.customer_driver) +
            getIsInternalCommunicationSuffix(appointment.customer_driver)
        });
        whatsAppValue = getDefaultValueForEmailAndPhone(appointment.customer_driver.id, appointment.customer_driver.tel_mobile_business, whatsAppValue, false);
      }

      if (appointment.customer_driver.tel_mobile_private) {
        options.push({
          key: `${appointment.customer_driver.tel_mobile_private};${appointment.customer_driver.id}`,
          text: appointment.customer_driver.tel_mobile_private,
          value: `${appointment.customer_driver.tel_mobile_private};${appointment.customer_driver.id}`,
          disabled: appointment.customer_driver.block_communication || appointment.customer_driver.is_internal,
          description:
            t("v8_driver_private", "Driver private") +
            getBlockCommunicationSuffix(appointment.customer_driver) +
            getIsInternalCommunicationSuffix(appointment.customer_driver)
        });
        whatsAppValue = getDefaultValueForEmailAndPhone(appointment.customer_driver.id, appointment.customer_driver.tel_mobile_private, whatsAppValue, false);
      }
    }

    if (appointment.customer_contract?.id) {
      if (appointment.customer_contract.tel_mobile_business) {
        options.push({
          key: `${appointment.customer_contract.tel_mobile_business};${appointment.customer_contract.id}`,
          text: appointment.customer_contract.tel_mobile_business,
          value: `${appointment.customer_contract.tel_mobile_business};${appointment.customer_contract.id}`,
          disabled: appointment.customer_contract.block_communication || appointment.customer_contract.is_internal,
          description:
            t("v8_contract_business", "Contract business") +
            getBlockCommunicationSuffix(appointment.customer_contract) +
            getIsInternalCommunicationSuffix(appointment.customer_contract)
        });
        whatsAppValue = getDefaultValueForEmailAndPhone(appointment.customer_contract.id, appointment.customer_contract.tel_mobile_business, whatsAppValue, false);
      }

      if (appointment.customer_contract.tel_mobile_private) {
        options.push({
          key: `${appointment.customer_contract.tel_mobile_private};${appointment.customer_contract.id}`,
          text: appointment.customer_contract.tel_mobile_private,
          value: `${appointment.customer_contract.tel_mobile_private};${appointment.customer_contract.id}`,
          disabled: appointment.customer_contract.block_communication || appointment.customer_contract.is_internal,
          description:
            t("v8_contract_private", "Contract private") +
            getBlockCommunicationSuffix(appointment.customer_contract) +
            getIsInternalCommunicationSuffix(appointment.customer_contract)
        });
        whatsAppValue = getDefaultValueForEmailAndPhone(appointment.customer_contract.id, appointment.customer_contract.tel_mobile_private, whatsAppValue, false);
      }
    }

    if (appointment.customer_owner?.id) {
      if (appointment.customer_owner.tel_mobile_business) {
        options.push({
          key: `${appointment.customer_owner.tel_mobile_business};${appointment.customer_owner.id}`,
          text: appointment.customer_owner.tel_mobile_business,
          value: `${appointment.customer_owner.tel_mobile_business};${appointment.customer_owner.id}`,
          disabled: appointment.customer_owner.block_communication || appointment.customer_owner.is_internal,
          description:
            t("v8_owner_business", "Owner business") +
            getBlockCommunicationSuffix(appointment.customer_owner) +
            getIsInternalCommunicationSuffix(appointment.customer_owner)
        });
        whatsAppValue = getDefaultValueForEmailAndPhone(appointment.customer_owner.id, appointment.customer_owner.tel_mobile_business, whatsAppValue, false);
      }

      if (appointment.customer_owner.tel_mobile_private) {
        options.push({
          key: `${appointment.customer_owner.tel_mobile_private};${appointment.customer_owner.id}`,
          text: appointment.customer_owner.tel_mobile_private,
          value: `${appointment.customer_owner.tel_mobile_private};${appointment.customer_owner.id}`,
          disabled: appointment.customer_owner.block_communication || appointment.customer_owner.is_internal,
          description:
            t("v8_owner_private", "Owner private") +
            getBlockCommunicationSuffix(appointment.customer_owner) +
            getIsInternalCommunicationSuffix(appointment.customer_owner)
        });
        whatsAppValue = getDefaultValueForEmailAndPhone(appointment.customer_owner.id, appointment.customer_owner.tel_mobile_private, whatsAppValue, false);
      }
    }

    if (!whatsAppValue && !lastWhatsAppReceiver?.destination) whatsAppValue = DO_NOT_SEND_BY_VALUE;
    else if (lastWhatsAppReceiver?.destination && !whatsAppValue) {
      whatsAppValue = MANUAL_ENTRY_VALUE;
      manualWhatsAppSelected = true;
      manuallyEnteredWhatsApp = lastWhatsAppReceiver.destination || "";
    }

    setWhatsAppOptions(options);
    setFormState(pre => ({ ...pre, selectedWhatsApp: whatsAppValue, manualWhatsAppSelected, manuallyEnteredWhatsApp }));
  };

  const handleManualEmailChange = (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsInvalidEmail(false);
    setFormState(pre => ({ ...pre, manuallyEnteredEmail: evt.target.value }));
  };

  const handleManualPhoneNrChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setFormState(current => ({ ...current, manuallyEnteredPhoneNr: value, invalidPhone: !validatePhoneNr(value) }));
  };

  const handleManualWhatsAppChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setFormState(current => ({ ...current, manuallyEnteredWhatsApp: value, invalidWhatsApp: !validatePhoneNr(value) }));
  };

  const handleNoteChange = (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState(form => ({ ...form, note: evt.target.value }));
  };

  const handleCustomerDropdownChange = (selectedCustomerID: number = 0) => setFormState(form => ({ ...form, selectedCustomerID }));

  const handleEmailDropdownChange = (selectedEmail?: string) => {
    if (!selectedEmail) return;

    if (selectedEmail === MANUAL_ENTRY_VALUE) {
      setFormState(pre => {
        return {
          ...pre,
          manualEmailSelected: true,
          parsedCustomerEmail: "",
          selectedEmail,
          selectedCustomerID: 0,
          parsedCustomerPhone: "",
          selectedPhone: "",
          manuallyEnteredPhoneNr: "",
          manualPhoneNrSelected: false,
          parsedWhatsApp: "",
          selectedWhatsApp: "",
          manualWhatsAppSelected: false,
          manuallyEnteredWhatsApp: ""
        };
      });
      return;
    }

    if (selectedEmail === DO_NOT_SEND_BY_VALUE) {
      setFormState(pre => ({
        ...pre,
        selectedEmail: DO_NOT_SEND_BY_VALUE,
        parsedCustomerEmail: "",
        manualEmailSelected: false,
        manuallyEnteredEmail: ""
      }));
      return;
    }

    const splittedValue = selectedEmail.split(";");
    const customerEmail = splittedValue[0];
    let customerID = Number(splittedValue[1]);
    let parsedCustomerPhone = formState.parsedCustomerPhone;
    let selectedPhone = formState.selectedPhone;
    let parsedWhatsApp = formState.parsedWhatsApp;
    let selectedWhatsApp = formState.selectedWhatsApp;

    if (formState.parsedCustomerEmail === customerEmail && formState.selectedCustomerID === customerID) return;

    if (customerID > 0 && formState.selectedCustomerID !== customerID) {
      parsedCustomerPhone = "";
      selectedPhone = DO_NOT_SEND_BY_VALUE;
      parsedWhatsApp = "";
      selectedWhatsApp = DO_NOT_SEND_BY_VALUE;
    }

    if (!customerID && (parsedCustomerPhone || parsedWhatsApp)) customerID = formState.selectedCustomerID;

    setFormState(pre => {
      return {
        ...pre,
        manualEmailSelected: false,
        parsedCustomerEmail: customerEmail,
        selectedCustomerID: Number(customerID),
        selectedEmail: `${customerEmail};${customerID}`,
        parsedCustomerPhone,
        selectedPhone,
        manuallyEnteredPhoneNr: "",
        manualPhoneNrSelected: false,
        parsedWhatsApp,
        selectedWhatsApp,
        manualWhatsAppSelected: false,
        manuallyEnteredWhatsApp: ""
      };
    });
  };

  const handlePhoneDropdownChange = (selectedPhone?: string) => {
    if (!selectedPhone) return;

    if (selectedPhone === DO_NOT_SEND_BY_VALUE) {
      setFormState(pre => ({
        ...pre,
        selectedPhone,
        parsedCustomerPhone: "",
        manualPhoneNrSelected: false,
        manuallyEnteredPhoneNr: "",
        invalidPhone: false
      }));
      return;
    }

    const splittedValue = selectedPhone.split(";");
    const customerPhone = splittedValue[0];
    let customerID = Number(splittedValue[1]);

    let parsedWhatsApp = formState.parsedWhatsApp;
    let selectedWhatsApp = formState.selectedWhatsApp;
    let parsedCustomerEmail = formState.parsedCustomerEmail;
    let manualEmailSelected = formState.manualEmailSelected;

    if (customerID > 0 && formState.selectedCustomerID !== customerID) {
      parsedCustomerEmail = "";
      manualEmailSelected = false;
      parsedWhatsApp = "";
      selectedWhatsApp = DO_NOT_SEND_BY_VALUE;
    }

    if (!customerID && parsedCustomerEmail) customerID = formState.selectedCustomerID;

    if (selectedPhone === MANUAL_ENTRY_VALUE)
      setFormState(current => ({
        ...current,
        manualPhoneNrSelected: true,
        manualEmailSelected: false,
        manuallyEnteredEmail: "",
        selectedEmail: "",
        selectedPhone,
        parsedWhatsApp: "",
        selectedWhatsApp: DO_NOT_SEND_BY_VALUE,
        manualWhatsAppSelected: false,
        manuallyEnteredWhatsApp: ""
      }));
    else {
      setFormState(pre => ({
        ...pre,
        manualEmailSelected,
        parsedCustomerEmail,
        parsedCustomerPhone: customerPhone,
        selectedCustomerID: Number(customerID),
        selectedPhone: `${customerPhone};${customerID}`,
        manuallyEnteredPhoneNr: "",
        manualPhoneNrSelected: false,
        invalidPhone: false,
        parsedWhatsApp,
        selectedWhatsApp
      }));
    }
  };

  const handleWhatsAppDropdownChange = (selectedWhatsApp?: string) => {
    if (!selectedWhatsApp) return;

    if (selectedWhatsApp === DO_NOT_SEND_BY_VALUE) {
      setFormState(pre => ({
        ...pre,
        selectedWhatsApp,
        parsedWhatsApp: "",
        manualWhatsAppSelected: false,
        manuallyEnteredWhatsApp: "",
        invalidWhatsApp: false
      }));
      return;
    }

    const splittedValue = selectedWhatsApp.split(";");
    const customerWhatsApp = splittedValue[0];
    let customerID = Number(splittedValue[1]);

    let parsedCustomerEmail = formState.parsedCustomerEmail;
    let manualEmailSelected = formState.manualEmailSelected;
    let parsedCustomerPhone = formState.parsedCustomerPhone;
    let selectedPhone = formState.selectedPhone;

    if (customerID > 0 && formState.selectedCustomerID !== customerID) {
      parsedCustomerEmail = "";
      manualEmailSelected = false;
      parsedCustomerPhone = "";
      selectedPhone = DO_NOT_SEND_BY_VALUE;
    }

    if (!customerID && parsedCustomerEmail) customerID = formState.selectedCustomerID;

    if (selectedWhatsApp === MANUAL_ENTRY_VALUE)
      setFormState(current => ({
        ...current,
        manualWhatsAppSelected: true,
        manualEmailSelected: false,
        manuallyEnteredEmail: "",
        selectedEmail: "",
        manualPhoneNrSelected: false,
        manuallyEnteredPhoneNr: "",
        selectedPhone: DO_NOT_SEND_BY_VALUE,
        selectedWhatsApp
      }));
    else {
      setFormState(pre => ({
        ...pre,
        manualEmailSelected,
        parsedCustomerEmail,
        parsedCustomerPhone,
        selectedPhone,
        parsedWhatsApp: customerWhatsApp,
        selectedCustomerID: Number(customerID),
        selectedWhatsApp: `${customerWhatsApp};${customerID}`,
        manuallyEnteredWhatsApp: "",
        manualWhatsAppSelected: false,
        invalidWhatsApp: false
      }));
    }
  };

  const renderFields = () => {
    return (
      <Form>
        <Grid className="inline-field">
          {selectedLocation?.third_party_send_communications && (
            <Grid.Row>
              <Grid.Column className={`CustomerCommunicationModal-input-field`}>
                <Form.Field required>
                  <label>{t("v8_select_customer", "Select customer")}</label>
                  <Dropdown
                    options={getCustomerOptions(appointment)}
                    fluid
                    search
                    selection
                    className={formState.invalidCustomer ? "error" : ""}
                    value={formState.selectedCustomerID}
                    name="selectCustomer"
                    placeholder={t("v8_select_customer", "Select customer")}
                    onChange={handleCustomerDropdownChange}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
          )}

          {!selectedLocation?.third_party_send_communications && (
            <>
              <Grid.Row>
                <Grid.Column className="CustomerCommunicationModal-input-field">
                  <Form.Field>
                    <label>
                      {t("v8_select_email", "Select email")}
                      <PreferredChannelIcon isPreferred={preferredChannel === COMMUNICATION_CHANNELS.Email} />
                    </label>
                    <Dropdown
                      fluid
                      search
                      name="selectEmail"
                      selection
                      value={formState.selectedEmail}
                      className={formState.invalidEmail ? "error" : ""}
                      placeholder={t("v8_select_email", "Select email")}
                      options={emailOptions}
                      disabled={formState.manualPhoneNrSelected || formState.manualWhatsAppSelected}
                      onChange={handleEmailDropdownChange}
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              {formState.manualEmailSelected && (
                <Grid.Row>
                  <Grid.Column className="CustomerCommunicationModal-input-field">
                    <Form.Field>
                      <label>{t("v8_email", "Email")}</label>
                      <Input
                        name="email"
                        value={formState.manuallyEnteredEmail}
                        onChange={handleManualEmailChange}
                        required
                        type="email"
                        placeholder={t("v8_enter_email", "Enter email")}
                        className={formState.invalidManualEmail ? "error" : ""}
                      />
                    </Form.Field>
                    {isInvalidEmail && <span className="error">{t("v8_invalid_email", "Please enter a valid email address")}</span>}
                  </Grid.Column>
                </Grid.Row>
              )}

              {shouldRenderPhoneNumberField && (
                <Grid.Row>
                  <Grid.Column className="CustomerCommunicationModal-input-field">
                    <Form.Field>
                      <label>
                        {t("v8_select_phone_number", "Select phone number")}
                        <PreferredChannelIcon isPreferred={preferredChannel === COMMUNICATION_CHANNELS.SMS} />
                      </label>
                      <Dropdown
                        fluid
                        name="selectPhone"
                        value={formState.selectedPhone}
                        selection
                        disabled={formState.manualEmailSelected || formState.manualWhatsAppSelected}
                        className={formState.invalidPhone ? "error" : ""}
                        options={phoneOptions}
                        placeholder={t("v8_do_not_send_by_sms", "Do not send by SMS")}
                        onChange={handlePhoneDropdownChange}
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
              )}

              {formState.manualPhoneNrSelected && (
                <Grid.Row>
                  <Grid.Column className="CustomerCommunicationModal-input-field">
                    <Form.Field>
                      <label>{t("v8_phone_nr", "Phone Number")}</label>
                      <div className="CustomerCommunicationModal-phoneField">
                        <Input
                          name="phone"
                          value={formState.manuallyEnteredPhoneNr}
                          onChange={handleManualPhoneNrChange}
                          required
                          type="text"
                          placeholder={t("v8_enter_phone_nr", "Enter Phone Number")}
                          className={formState.invalidPhone ? "error" : ""}
                        />
                        {formState.invalidPhone && <span className="error invalid-phone-num">{t("v8_invalid_phone_nr", "Please enter a valid phone number")}</span>}
                      </div>
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
              )}

              {shouldRenderWhatsAppField && (
                <Grid.Row>
                  <Grid.Column className="CustomerCommunicationModal-input-field">
                    <Form.Field>
                      <label>
                        {t("v8_select_whatsapp_number", "Select WhatsApp number")}
                        <PreferredChannelIcon isPreferred={preferredChannel === COMMUNICATION_CHANNELS.WhatsApp} />
                      </label>
                      <Dropdown
                        fluid
                        name="selectWhatsApp"
                        value={formState.selectedWhatsApp}
                        selection
                        disabled={formState.manualEmailSelected || formState.manualPhoneNrSelected}
                        className={formState.invalidWhatsApp ? "error" : ""}
                        options={whatsAppOptions}
                        placeholder={t("v8_do_not_send_by_whatsapp", "Do not send by WhatsApp")}
                        onChange={handleWhatsAppDropdownChange}
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
              )}

              {formState.manualWhatsAppSelected && (
                <Grid.Row>
                  <Grid.Column className="CustomerCommunicationModal-input-field">
                    <Form.Field>
                      <label>{t("v8_whatsapp_number", "WhatsApp Number")}</label>
                      <div className="CustomerCommunicationModal-phoneField">
                        <Input
                          name="whatsapp"
                          value={formState.manuallyEnteredWhatsApp}
                          onChange={handleManualWhatsAppChange}
                          required
                          type="text"
                          placeholder={t("v8_enter_whatsapp_nr", "Enter WhatsApp Number")}
                          className={formState.invalidWhatsApp ? "error" : ""}
                        />
                        {formState.invalidWhatsApp && <span className="error invalid-phone-num">{t("v8_invalid_phone_nr", "Please enter a valid phone number")}</span>}
                      </div>
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
              )}
            </>
          )}

          <Grid.Row>
            <Grid.Column className="CustomerCommunicationModal-note-field CustomerCommunicationModal-input-field">
              <Form.Field>
                <label>{t("v8_note", "Note")}</label>
                <TextArea rows={2} name="note" value={formState.note} onChange={handleNoteChange} required type="email" placeholder={t("v8_write_note", "Write note")} />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    );
  };

  const showRepairOverviewModal = isCarReadyOrQualityCheck(appointment.appointment_status_identifier);

  const switchShowInterventionsVat = () => setShowInterventionsVat(previous => !previous);
  const switchShowReplacementsVat = () => setShowReplacementsVat(previous => !previous);

  const renderInterventions = () => {
    if (showRepairOverviewModal) return <RepairOverviewInterventions appointment={appointment} />;

    const visibleTotals = getTotals(
      appointment.interventions?.filter(i => i.visible_to_customer && !isHiddenPriceIntervention(i, selectedLocation)) ?? [],
      appointment.car?.tyres?.flatMap(t => t.replacements?.filter(r => r.visible_to_customer) ?? []) ?? [],
      selectedLocation,
      showInterventionsVat,
      showReplacementsVat
    );

    return (
      <>
        <CustomerCommunicationInterventions
          appointment={appointment}
          showVat={showInterventionsVat}
          invalidContent={formState.invalidContent}
          onSwitchShowVat={switchShowInterventionsVat}
        />
        <CustomerCommunicationReplacements
          appointment={appointment}
          showVat={showReplacementsVat}
          invalidContent={formState.invalidContent}
          onSwitchShowVat={switchShowReplacementsVat}
        />
        <CustomerCommunicationPrice totals={visibleTotals} showVat={showInterventionsVat || showReplacementsVat} />
      </>
    );
  };

  const renderContent = () => {
    return (
      <>
        {renderFields()}
        <div className="CustomerCommunicationModal-divider" />
        {renderInterventions()}
      </>
    );
  };

  const getSaveButtonName = () => {
    if (send === CUSTOMER_COMMUNICATIONS.RepairOverview) return t("v8_send_repair_overview", "Send repair overview");
    if (send === CUSTOMER_COMMUNICATIONS.DiagnoseOverview) return t("v8_send_diagnose_overview", "Send diagnose overview");
    return t("v8_send_online_check_in", "Send online check-in");
  };

  const canSendCommunication = () => {
    const hasInterventionOrReplacementsVisibleToCustomer =
      appointment.interventions?.some(intervention => intervention.visible_to_customer) ||
      appointment.car?.tyres?.some(t => t.replacements?.some(r => r.visible_to_customer));

    if (formState.manualEmailSelected) {
      if (!formState.manuallyEnteredEmail || !validateEmail(formState.manuallyEnteredEmail)) {
        setFormState(pre => ({ ...pre, invalidManualEmail: true }));
        return false;
      }
    }

    if (formState.manualPhoneNrSelected && (!formState.manuallyEnteredPhoneNr || !validatePhoneNr(formState.manuallyEnteredPhoneNr))) {
      setFormState(current => ({ ...current, invalidPhone: true }));
      return false;
    }

    if (formState.manualWhatsAppSelected && (!formState.manuallyEnteredWhatsApp || !validatePhoneNr(formState.manuallyEnteredWhatsApp))) {
      setFormState(current => ({ ...current, invalidWhatsApp: true }));
      return false;
    }

    if (selectedLocation?.third_party_send_communications && !formState.selectedCustomerID) {
      setFormState(pre => ({ ...pre, invalidCustomer: true }));
      return false;
    } else if (
      !selectedLocation?.third_party_send_communications &&
      formState.selectedEmail === DO_NOT_SEND_BY_VALUE &&
      formState.selectedPhone === DO_NOT_SEND_BY_VALUE &&
      formState.selectedWhatsApp === DO_NOT_SEND_BY_VALUE
    ) {
      setFormState(pre => ({ ...pre, invalidPhone: true, invalidEmail: true }));
      return false;
    } else if (!hasInterventionOrReplacementsVisibleToCustomer) {
      setFormState(pre => ({ ...pre, invalidContent: true }));
      return false;
    }

    setFormState(pre => ({
      ...pre,
      invalidPhone: false,
      invalidEmail: false,
      invalidWhatsApp: false,
      invalidCustomer: false,
      invalidContent: false,
      invalidManualEmail: false
    }));

    return true;
  };

  const getReceivers = () => {
    const receivers: Receivers[] = [];

    if (formState.parsedCustomerEmail || formState.manualEmailSelected) {
      receivers.push({
        destination: formState.manualEmailSelected ? formState.manuallyEnteredEmail : formState.parsedCustomerEmail,
        channel: COMMUNICATION_CHANNELS.Email
      });
    }

    if (formState.parsedCustomerPhone || formState.manualPhoneNrSelected) {
      receivers.push({
        destination: formState.manualPhoneNrSelected ? formState.manuallyEnteredPhoneNr : formState.parsedCustomerPhone,
        channel: COMMUNICATION_CHANNELS.SMS
      });
    }

    if (formState.parsedWhatsApp || formState.manualWhatsAppSelected) {
      receivers.push({
        destination: formState.manualWhatsAppSelected ? formState.manuallyEnteredWhatsApp : formState.parsedWhatsApp,
        channel: COMMUNICATION_CHANNELS.WhatsApp
      });
    }

    if (selectedLocation?.third_party_send_communications && formState.selectedCustomerID > 0) {
      const customerType =
        [
          { id: appointment.customer_driver_id, type: "driver" },
          { id: appointment.customer_owner_id, type: "owner" }
        ].find(item => item.id === formState.selectedCustomerID)?.type || "contractor";

      receivers.push({
        destination: customerType,
        channel: COMMUNICATION_CHANNELS.Webhook
      });
    }
    return receivers;
  };

  const handleSaveCommunication = async () => {
    if (!canSendCommunication()) return;
    setIsLoading(true);

    const payload = {
      communication_type: send,
      appointment_id: appointment.id,
      note: formState.note,
      customer_id: formState.manualEmailSelected || formState.manualPhoneNrSelected || formState.manualWhatsAppSelected ? null : formState.selectedCustomerID,
      receivers: getReceivers(),
      question_result_ids: checks?.flatMap(check => check.question_items.map(questionItem => questionItem.id)) ?? []
    };

    try {
      await saveCustomerCommunicationMutation.mutateAsync(payload);
      setIsLoading(false);
      onClose?.();
    } catch (err) {
      toast.error(t("v8_something_went_wrong", "Something went wrong, please try again."));
      setIsLoading(false);
      throw err;
    }
  };

  const isSaveButtonDisabled = () => {
    let isSaveButtonDisabled = false;
    if (formState.manualEmailSelected && !formState.manuallyEnteredEmail) isSaveButtonDisabled = true;
    if (formState.manualPhoneNrSelected && (!formState.manuallyEnteredPhoneNr || !validatePhoneNr(formState.manuallyEnteredPhoneNr))) isSaveButtonDisabled = true;
    if (formState.manualWhatsAppSelected && (!formState.manuallyEnteredWhatsApp || !validatePhoneNr(formState.manuallyEnteredWhatsApp))) isSaveButtonDisabled = true;

    if (
      !formState.manualEmailSelected &&
      !formState.manualPhoneNrSelected &&
      !formState.manualWhatsAppSelected &&
      !formState.parsedCustomerEmail &&
      !formState.parsedCustomerPhone &&
      !formState.parsedWhatsApp &&
      !selectedLocation?.third_party_send_communications
    )
      isSaveButtonDisabled = true;

    if (
      !selectedLocation?.third_party_send_communications &&
      appointmentDealer?.sms_gateway_id &&
      appointmentDealer.sms_gateway_id > SMS_GATEWAYS.Disabled &&
      formState.selectedEmail === DO_NOT_SEND_BY_VALUE &&
      formState.selectedPhone === DO_NOT_SEND_BY_VALUE &&
      formState.selectedWhatsApp === DO_NOT_SEND_BY_VALUE
    )
      isSaveButtonDisabled = true;

    return isSaveButtonDisabled;
  };

  useEffect(() => {
    setEmailOptionsAndValue(appointment);
    setPhoneOptionsAndValue(appointment);
    setWhatsAppOptionsAndValue(appointment);
  }, []);

  return (
    <Modal open size="fullscreen" onClose={onClose} className="CustomerCommunicationModal">
      <Modal.Header className="CustomerCommunicationModal-header">
        <h1>{t("v8_customer_communication", "Customer Communication")}</h1>
        <span onClick={onClose}>
          <Icon name="close" />
        </span>
      </Modal.Header>
      <Modal.Content className="CustomerCommunicationModal-content">{renderContent()}</Modal.Content>
      <Modal.Actions className="CustomerCommunicationModal-action">
        <div className="inner">
          <Button loading={isLoading} disabled={isSaveButtonDisabled()} color="green" className="save-btn" onClick={handleSaveCommunication}>
            {getSaveButtonName()}
            <Icon className="check" />
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  );
};
